import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const EMPTY_FORM = { name: '', email: '', password: '', funcao: '', role: 'operator', is_active: true };

function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-lg shadow-xl">
        <div className="flex items-center justify-between p-6 border-b border-gray-100">
          <h3 className="font-semibold text-gray-900">{title}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
}

function UserForm({ initial, onSave, onClose, loading }) {
  const [form, setForm] = useState(initial);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(form);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Nome</label>
        <input value={form.name} onChange={e => set('name', e.target.value)} required
          className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Função <span className="text-gray-400 font-normal">(ex: Designer, Copywriter…)</span></label>
        <input value={form.funcao || ''} onChange={e => set('funcao', e.target.value)}
          placeholder="Cargo ou especialidade"
          className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
        <input type="email" value={form.email} onChange={e => set('email', e.target.value)} required
          className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Senha {initial.id && <span className="text-gray-400 font-normal">(deixe em branco para não alterar)</span>}
        </label>
        <input type="password" value={form.password} onChange={e => set('password', e.target.value)}
          required={!initial.id} minLength={6}
          className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder={initial.id ? '••••••••' : ''} />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Perfil</label>
        <select value={form.role} onChange={e => set('role', e.target.value)}
          className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
          <option value="operator">Operador</option>
          <option value="admin">Administrador</option>
        </select>
      </div>
      {initial.id && (
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={form.is_active} onChange={e => set('is_active', e.target.checked)}
            className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
          <span className="text-sm text-gray-700">Conta ativa</span>
        </label>
      )}
      <div className="flex gap-3 pt-2">
        <button type="button" onClick={onClose}
          className="flex-1 border border-gray-200 text-gray-700 py-2.5 rounded-xl text-sm font-medium hover:bg-gray-50 transition">
          Cancelar
        </button>
        <button type="submit" disabled={loading}
          className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white py-2.5 rounded-xl text-sm font-medium transition">
          {loading ? 'Salvando...' : 'Salvar'}
        </button>
      </div>
    </form>
  );
}

function PermissionsModal({ user, allPermissions, onClose }) {
  const [selected, setSelected] = useState(user.permissions || []);
  const [saving, setSaving] = useState(false);

  const toggle = (key) => {
    setSelected(s => s.includes(key) ? s.filter(k => k !== key) : [...s, key]);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/users/${user.id}/permissions`, { permissions: selected });
      onClose(selected);
    } catch (err) {
      alert(err.response?.data?.error || 'Erro ao salvar permissões');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title={`Permissões — ${user.name}`} onClose={() => onClose(null)}>
      {user.role === 'admin' ? (
        <p className="text-sm text-gray-500">Administradores têm acesso total ao sistema.</p>
      ) : (
        <>
          <div className="space-y-2 max-h-72 overflow-y-auto mb-4">
            {allPermissions.map(p => (
              <label key={p.key} className="flex items-start gap-3 p-3 rounded-xl border border-gray-100 hover:border-blue-200 hover:bg-blue-50/40 cursor-pointer transition">
                <input type="checkbox" checked={selected.includes(p.key)} onChange={() => toggle(p.key)}
                  className="w-4 h-4 mt-0.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                <div>
                  <p className="text-sm font-medium text-gray-900">{p.label}</p>
                  {p.description && <p className="text-xs text-gray-500 mt-0.5">{p.description}</p>}
                </div>
              </label>
            ))}
          </div>
          <div className="flex gap-3">
            <button onClick={() => onClose(null)}
              className="flex-1 border border-gray-200 text-gray-700 py-2.5 rounded-xl text-sm font-medium hover:bg-gray-50 transition">
              Cancelar
            </button>
            <button onClick={save} disabled={saving}
              className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white py-2.5 rounded-xl text-sm font-medium transition">
              {saving ? 'Salvando...' : 'Salvar permissões'}
            </button>
          </div>
        </>
      )}
    </Modal>
  );
}

export default function UsersPage({ onUsersChange }) {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formLoading, setFormLoading] = useState(false);
  const [error, setError] = useState('');
  const [modal, setModal] = useState(null); // 'create' | { edit: user } | { perms: user }
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = useCallback(async () => {
    try {
      const [u, p] = await Promise.all([api.get('/users'), api.get('/permissions')]);
      setUsers(u.data);
      setPermissions(p.data);
    } catch {
      setError('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async (form) => {
    setFormLoading(true);
    try {
      const res = await api.post('/users', form);
      const updated = [...users, res.data];
      setUsers(updated);
      onUsersChange?.(updated);
      setModal(null);
    } catch (err) {
      alert(err.response?.data?.error || 'Erro ao criar usuário');
    } finally {
      setFormLoading(false);
    }
  };

  const handleEdit = async (form) => {
    setFormLoading(true);
    try {
      const payload = { ...form };
      if (!payload.password) delete payload.password;
      const res = await api.put(`/users/${modal.edit.id}`, payload);
      const updated = users.map(x => x.id === modal.edit.id ? { ...x, ...res.data } : x);
      setUsers(updated);
      onUsersChange?.(updated);
      setModal(null);
    } catch (err) {
      alert(err.response?.data?.error || 'Erro ao atualizar usuário');
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/users/${deleteTarget.id}`);
      const updated = users.filter(x => x.id !== deleteTarget.id);
      setUsers(updated);
      onUsersChange?.(updated);
      setDeleteTarget(null);
    } catch (err) {
      alert(err.response?.data?.error || 'Erro ao excluir usuário');
    }
  };

  const handlePermsSaved = (permsUser, newPerms) => {
    if (newPerms !== null) {
      setUsers(u => u.map(x => x.id === permsUser.id ? { ...x, permissions: newPerms } : x));
    }
    setModal(null);
  };

  const roleBadge = (role) => role === 'admin'
    ? <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700">Admin</span>
    : <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">Operador</span>;

  const statusBadge = (active) => active
    ? <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">Ativo</span>
    : <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500">Inativo</span>;

  if (loading) return <div className="flex items-center justify-center h-64 text-gray-400 text-sm">Carregando...</div>;
  if (error) return <div className="text-red-500 text-sm p-4">{error}</div>;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Usuários</h2>
          <p className="text-sm text-gray-500 mt-0.5">{users.length} usuário{users.length !== 1 ? 's' : ''} cadastrado{users.length !== 1 ? 's' : ''}</p>
        </div>
        <button onClick={() => setModal('create')}
          className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-4 py-2.5 rounded-xl transition flex items-center gap-2">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Novo usuário
        </button>
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-100 bg-gray-50/50">
              <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-6 py-3.5">Usuário</th>
              <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-6 py-3.5">Perfil</th>
              <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-6 py-3.5">Status</th>
              <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-6 py-3.5">Permissões</th>
              <th className="px-6 py-3.5"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {users.map(u => (
              <tr key={u.id} className="hover:bg-gray-50/50 transition">
                <td className="px-6 py-4">
                  <div>
                    <p className="text-sm font-medium text-gray-900">{u.name}</p>
                    {u.funcao && <p className="text-xs text-indigo-500 font-medium">{u.funcao}</p>}
                    <p className="text-xs text-gray-400">{u.email}</p>
                  </div>
                </td>
                <td className="px-6 py-4">{roleBadge(u.role)}</td>
                <td className="px-6 py-4">{statusBadge(u.is_active)}</td>
                <td className="px-6 py-4">
                  {u.role === 'admin' ? (
                    <span className="text-xs text-gray-400">Acesso total</span>
                  ) : (
                    <button onClick={() => setModal({ perms: u })}
                      className="text-xs text-blue-600 hover:text-blue-700 font-medium">
                      {u.permissions?.length ?? 0} permissão{(u.permissions?.length ?? 0) !== 1 ? 'ões' : ''} →
                    </button>
                  )}
                </td>
                <td className="px-6 py-4">
                  <div className="flex items-center justify-end gap-2">
                    <button onClick={() => setModal({ edit: { ...u, password: '' } })}
                      className="text-xs text-gray-500 hover:text-gray-700 font-medium px-3 py-1.5 rounded-lg hover:bg-gray-100 transition">
                      Editar
                    </button>
                    {u.id !== me?.id && (
                      <button onClick={() => setDeleteTarget(u)}
                        className="text-xs text-red-500 hover:text-red-700 font-medium px-3 py-1.5 rounded-lg hover:bg-red-50 transition">
                        Excluir
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {modal === 'create' && (
        <Modal title="Novo usuário" onClose={() => setModal(null)}>
          <UserForm initial={EMPTY_FORM} onSave={handleCreate} onClose={() => setModal(null)} loading={formLoading} />
        </Modal>
      )}

      {modal?.edit && (
        <Modal title="Editar usuário" onClose={() => setModal(null)}>
          <UserForm initial={modal.edit} onSave={handleEdit} onClose={() => setModal(null)} loading={formLoading} />
        </Modal>
      )}

      {modal?.perms && (
        <PermissionsModal
          user={modal.perms}
          allPermissions={permissions}
          onClose={(newPerms) => handlePermsSaved(modal.perms, newPerms)}
        />
      )}

      {deleteTarget && (
        <Modal title="Confirmar exclusão" onClose={() => setDeleteTarget(null)}>
          <p className="text-sm text-gray-600 mb-6">
            Tem certeza que deseja excluir <strong>{deleteTarget.name}</strong>? Esta ação não pode ser desfeita.
          </p>
          <div className="flex gap-3">
            <button onClick={() => setDeleteTarget(null)}
              className="flex-1 border border-gray-200 text-gray-700 py-2.5 rounded-xl text-sm font-medium hover:bg-gray-50 transition">
              Cancelar
            </button>
            <button onClick={handleDelete}
              className="flex-1 bg-red-600 hover:bg-red-700 text-white py-2.5 rounded-xl text-sm font-medium transition">
              Excluir
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}
